<template>
    <page-container class="h-full">
        <page-header> Local Performance </page-header>

        <div v-if="loading" class="flex items-center justify-center h-96">
            <vx-icon-loading class="w-8 h-8 text-sky-500" />
        </div>

        <vx-alert v-else-if="error" color="danger" :closeable="false">
            <div class="flex items-center justify-between">
                <span>{{ error }}</span>
                <vx-button data-test="retry-button" size="sm" color="danger" @click="fetchEmbedUrl"> Retry </vx-button>
            </div>
        </vx-alert>

        <iframe
            v-else-if="embedUrl"
            ref="dashboardIframe"
            :src="embedUrl"
            class="w-full min-h-[500px] border-0 rounded-lg"
            allow="fullscreen"
            referrerpolicy="strict-origin-when-cross-origin"
        />
    </page-container>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { VxAlert, VxButton, VxIconLoading } from '@voxie/frontend-components';
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { omniClient } from '~/services/omniClient';
import type { OmniEmbedEventData, OmniSizeEventPayload } from '~/types/omni.types';

const teamId = Spark.state.currentTeam.id;

const loading = ref(true);
const error = ref<string | null>(null);
const embedUrl = ref<string | null>(null);
const dashboardIframe = ref<HTMLIFrameElement | null>(null);

const fetchEmbedUrl = async () => {
    loading.value = true;
    error.value = null;

    try {
        const response = await omniClient.getEmbedUrl(teamId);
        embedUrl.value = response.data.url;
    } catch (err) {
        error.value = 'Unable to load analytics. Please try again later.';
        console.error('Failed to fetch Omni embed URL:', err);
    } finally {
        loading.value = false;
    }
};

const setFrameHeight = (height: number) => {
    if (dashboardIframe.value) {
        dashboardIframe.value.style.height = `${height}px`;
    }
};

const listener = (event: MessageEvent<OmniEmbedEventData<OmniSizeEventPayload>>) => {
    // Only accept messages from Omni
    if (event.data?.source !== 'omni') {
        return;
    }

    switch (event.data.name) {
        case 'size': {
            const { height } = event.data.payload;
            if (height) {
                setFrameHeight(height);
            }
            break;
        }
    }
};

onMounted(() => {
    fetchEmbedUrl();
    window.addEventListener('message', listener);
});

onUnmounted(() => {
    window.removeEventListener('message', listener);
});
</script>
